import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (!bytes || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function formatKB(kb: number): string {
  if (!kb || kb <= 0) return '0 B';
  if (kb < 1024) return `${kb} KB`;
  if (kb < 1024 * 1024) return `${(kb / 1024).toFixed(1)} MB`;
  return `${(kb / (1024 * 1024)).toFixed(1)} GB`;
}

function formatMB(mb: number): string {
  if (!mb || mb <= 0) return '0 MB';
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb.toFixed(0)} MB`;
}

function parsePct(pct: string | undefined): number {
  if (!pct) return 0;
  return parseInt(pct.replace('%', ''), 10) || 0;
}

/** Compute a simple "next run" from a cron pattern relative to now */
function nextCronRun(schedule: string): string {
  const parts = schedule.split(/\s+/);
  if (parts.length < 5) return 'Unknown';
  const [min, hour, , , dow] = parts;
  const now = new Date();
  const target = new Date();
  target.setHours(parseInt(hour, 10), parseInt(min, 10), 0, 0);

  if (dow !== '*') {
    const targetDow = parseInt(dow, 10);
    let daysAhead = (targetDow - now.getDay() + 7) % 7;
    if (daysAhead === 0 && target <= now) daysAhead = 7;
    target.setDate(now.getDate() + daysAhead);
  } else {
    if (target <= now) target.setDate(target.getDate() + 1);
  }

  const diff = target.getTime() - now.getTime();
  const hrs = Math.floor(diff / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);

  if (hrs > 24) {
    const days = Math.floor(hrs / 24);
    return `in ${days}d ${hrs % 24}h`;
  }
  return `in ${hrs}h ${mins}m`;
}

/** Parse crontab -l output into structured jobs */
function parseCrontab(raw: string): { schedule: string; command: string; description: string }[] {
  if (!raw) return [];
  const lines = raw.split('\n').filter(l => l.trim() && !l.trim().startsWith('#'));
  return lines.map(line => {
    const match = line.match(/^(\S+\s+\S+\s+\S+\s+\S+\s+\S+)\s+(.+)$/);
    if (!match) return null;
    const schedule = match[1];
    const cmd = match[2];
    let description = cmd;
    if (cmd.includes('daily_cleanup')) description = 'Daily maintenance (cleanup, log rotation, health check)';
    else if (cmd.includes('weekly_health_report')) description = 'Weekly system health report';
    return { schedule, command: cmd, description };
  }).filter(Boolean) as { schedule: string; command: string; description: string }[];
}

// ─── Animations ──────────────────────────────────────────────────────────────

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const fadeUp = {
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

const stagger = {
  animate: { transition: { staggerChildren: 0.06 } },
};

// ─── Styled Components ──────────────────────────────────────────────────────

const Container = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 1.7rem;
  color: #1e293b;
  margin: 0;
  font-weight: 700;
`;

const Subtitle = styled.p`
  margin: 4px 0 0;
  color: #64748b;
  font-size: 0.9rem;
`;

const RefreshButton = styled.button<{ $spinning?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #3b82f6, #6366f1);
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(99, 102, 241, 0.35);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
    animation: ${({ $spinning }) => ($spinning ? spin : 'none')} 0.8s linear infinite;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.15rem;
  font-weight: 700;
  color: #1e293b;
  margin: 32px 0 16px;
  padding-left: 14px;
  border-left: 4px solid;
  border-image: linear-gradient(180deg, #3b82f6, #6366f1) 1;
`;

const Grid = styled(motion.div)<{ $cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols || 3}, 1fr);
  gap: 18px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(motion.div)`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 22px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const CardTitle = styled.h3`
  font-size: 0.82rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #94a3b8;
  margin: 0 0 16px;
`;

const RingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const RingSvg = styled.svg`
  flex-shrink: 0;
`;

const RingLabel = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;

  span {
    display: block;
    font-size: 0.78rem;
    font-weight: 400;
    color: #94a3b8;
  }
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  font-size: 0.875rem;
  color: #475569;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
`;

const StatLabel = styled.span`
  color: #94a3b8;
`;

const StatValue = styled.span`
  font-weight: 600;
  color: #1e293b;
`;

const StatusDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  margin-right: 8px;
  box-shadow: 0 0 6px ${({ $color }) => $color}40;
`;

const ServiceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 0.875rem;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
`;

const ServiceName = styled.span`
  color: #334155;
  font-weight: 500;
  display: flex;
  align-items: center;
`;

const ServiceStatus = styled.span<{ $active: boolean }>`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${({ $active }) => ($active ? '#22c55e' : '#ef4444')};
`;

// ─── RAM Budget Bar ──────────────────────────────────────────────────────────

const BudgetBarWrapper = styled.div`
  margin-top: 8px;
`;

const BarOuter = styled.div`
  width: 100%;
  height: 32px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  background: #f1f5f9;
`;

const BarSegment = styled(motion.div)<{ $color: string; $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  min-width: ${({ $pct }) => ($pct > 4 ? 'auto' : '0')};
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 14px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.82rem;
  color: #475569;
`;

const LegendSwatch = styled.span<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: ${({ $color }) => $color};
`;

// ─── Progress / Bars ─────────────────────────────────────────────────────────

const BarTrack = styled.div`
  width: 100%;
  height: 8px;
  background: #f1f5f9;
  border-radius: 4px;
  overflow: hidden;
  margin: 6px 0;
`;

const BarFill = styled(motion.div)<{ $color: string }>`
  height: 100%;
  border-radius: 4px;
  background: ${({ $color }) => $color};
`;

// ─── Log Section ─────────────────────────────────────────────────────────────

const TabBar = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 18px;
  background: #f1f5f9;
  border-radius: 10px;
  padding: 4px;
  width: fit-content;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 8px 20px;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: ${({ $active }) => ($active ? '#fff' : 'transparent')};
  color: ${({ $active }) => ($active ? '#1e293b' : '#64748b')};
  box-shadow: ${({ $active }) => ($active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none')};

  &:hover {
    color: #1e293b;
  }
`;

const LogCard = styled(motion.div)`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  margin-bottom: 10px;
  overflow: hidden;
`;

const LogHeader = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  color: #334155;

  &:hover {
    background: #f8fafc;
  }
`;

const LogChevron = styled.span<{ $open: boolean }>`
  display: inline-block;
  transition: transform 0.2s;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  color: #94a3b8;
`;

const LogContent = styled(motion.pre)`
  margin: 0;
  padding: 16px 18px;
  background: #0f172a;
  color: #e2e8f0;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.78rem;
  line-height: 1.6;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

// ─── Cron Section ────────────────────────────────────────────────────────────

const CronTable = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
`;

const CronRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr 150px;
  gap: 12px;
  padding: 14px 18px;
  align-items: center;
  font-size: 0.85rem;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 4px;
  }
`;

const CronSchedule = styled.code`
  background: #f1f5f9;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.78rem;
  color: #6366f1;
  font-weight: 600;
`;

const CronDesc = styled.span`
  color: #475569;
`;

const CronNext = styled.span`
  color: #3b82f6;
  font-weight: 600;
  font-size: 0.8rem;
  text-align: right;

  @media (max-width: 640px) {
    text-align: left;
  }
`;

// ─── Skeleton ────────────────────────────────────────────────────────────────

const SkeletonBlock = styled.div<{ $w?: string; $h?: string }>`
  width: ${({ $w }) => $w || '100%'};
  height: ${({ $h }) => $h || '16px'};
  border-radius: 6px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.5s infinite;
`;

const SkeletonCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ErrorBanner = styled.div`
  padding: 16px 20px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  color: #991b1b;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
`;

// ─── Progress Ring Component ─────────────────────────────────────────────────

const ProgressRing: React.FC<{
  pct: number;
  size?: number;
  stroke?: number;
  color?: string;
  label: string;
  sub: string;
}> = ({ pct, size = 90, stroke = 8, color = '#3b82f6', label, sub }) => {
  const radius = (size - stroke) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (Math.min(pct, 100) / 100) * circ;

  return (
    <RingContainer>
      <RingSvg width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#f1f5f9"
          strokeWidth={stroke}
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1, ease: 'easeOut' }}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        <text
          x={size / 2}
          y={size / 2}
          textAnchor="middle"
          dy="0.35em"
          fontSize="1rem"
          fontWeight="700"
          fill="#1e293b"
        >
          {pct}%
        </text>
      </RingSvg>
      <RingLabel>
        {label}
        <span>{sub}</span>
      </RingLabel>
    </RingContainer>
  );
};

// ─── Loading Skeleton ────────────────────────────────────────────────────────

const LoadingSkeleton: React.FC = () => (
  <>
    <Grid $cols={3} as={motion.div} variants={stagger} initial="initial" animate="animate">
      {Array.from({ length: 5 }).map((_, i) => (
        <SkeletonCard key={i} as={motion.div} variants={fadeUp}>
          <SkeletonBlock $w="40%" $h="12px" />
          <SkeletonBlock $h="80px" />
          <SkeletonBlock $w="70%" $h="12px" />
          <SkeletonBlock $w="55%" $h="12px" />
        </SkeletonCard>
      ))}
    </Grid>
    <div style={{ marginTop: 32 }}>
      <SkeletonBlock $w="200px" $h="22px" />
      <div style={{ marginTop: 14 }}>
        <SkeletonBlock $h="32px" />
      </div>
    </div>
  </>
);

// ─── Main Component ──────────────────────────────────────────────────────────

const AppMaintenance: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [logTab, setLogTab] = useState<'cleanup' | 'health'>('cleanup');
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      const res = await axios.get('/api/admin/maintenance-logs', { withCredentials: true });

      if (res.data.success) {
        setData(res.data.data);
        // Auto-expand the most recent log in each category
        const expanded = new Set<string>();
        if (res.data.data.cleanupLogs?.[0]) {
          expanded.add(`cleanup-${res.data.data.cleanupLogs[0].date}`);
        }
        if (res.data.data.healthLogs?.[0]) {
          expanded.add(`health-${res.data.data.healthLogs[0].date}`);
        }
        setExpandedLogs(expanded);
      } else {
        setError('Failed to load maintenance data.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch maintenance data.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleLog = (key: string) => {
    setExpandedLogs((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ─── Derived values from actual API data ─────────────────────────────────

  const sys = data?.system || {};
  const ram = sys.ram || {};
  const disk = sys.disk || {};
  const swapLine = (() => {
    // Try to get swap from the free command output — backend may add it later
    // For now show from swap section if available
    return sys.swap || null;
  })();

  const ramTotalBytes = ram.total || 1;
  const ramUsedBytes = ram.used || 0;
  const ramFreeBytes = ram.free || 0;
  const ramAvailBytes = ram.available || 0;
  const ramPct = Math.round((ramUsedBytes / ramTotalBytes) * 100);

  const diskTotalKB = disk.totalKB || 1;
  const diskUsedKB = disk.usedKB || 0;
  const diskAvailKB = disk.availableKB || 0;
  const diskPct = parsePct(disk.usePercent);

  // PM2: API returns array of process objects
  const pm2Array = Array.isArray(data?.pm2) ? data.pm2 : [];

  // Services: API returns { mongod: 'active', nginx: 'active', ... }
  // pm2-ubuntu systemd service shows 'inactive' when PM2 was started manually (not via systemd)
  // This is normal — the service is enabled for boot but only activates on reboot.
  // If PM2 processes are running, show pm2-ubuntu as "running (manual)" instead of inactive.
  const services = data?.services || {};
  const pm2Running = pm2Array.length > 0 && pm2Array.some((p: any) => p.status === 'online');
  const serviceList = Object.entries(services).map(([name, status]) => {
    if (name === 'pm2-ubuntu' && status === 'inactive' && pm2Running) {
      return { name, status: 'active', note: 'PM2 running (started manually, systemd enabled for boot)' };
    }
    return { name, status: status as string, note: '' };
  });
  const backend = pm2Array.find((p: any) => p.name === 'backend') || pm2Array[0] || null;
  const backendMemMB = backend ? (backend.memory || 0) / (1024 * 1024) : 0;
  const backendUptimeHrs = backend?.uptime ? (Date.now() - backend.uptime) / 3600000 : 0;

  // MongoDB
  const mongo = data?.mongodb || {};
  const wtCache = mongo.wiredTigerCache || {};
  const cacheMB = (wtCache.currentBytes || 0) / (1024 * 1024);
  const cacheMaxMB = (wtCache.maxBytes || 1) / (1024 * 1024);
  const cachePct = Math.round((cacheMB / cacheMaxMB) * 100);
  const dbSizeMB = (mongo.dbStats?.dataSize || 0) / (1024 * 1024);
  const conns = mongo.connections || { current: 0, available: 0 };
  const sessionChunks = mongo.sessionChunks || { count: 0, size: 0 };
  const sessionRecs = mongo.sessionRecordings || {};
  const totalRecordings = Object.values(sessionRecs as Record<string, number>).reduce((a: number, b: number) => a + b, 0);

  // Cron jobs
  const cronJobs = parseCrontab(data?.cronJobs || '');

  // Cleanup & health logs
  const cleanupLogs: { date: string; content: string }[] = Array.isArray(data?.cleanupLogs) ? data.cleanupLogs : [];
  const healthLogs: { date: string; content: string }[] = Array.isArray(data?.healthLogs) ? data.healthLogs : [];

  // Load average
  const load = sys.load || {};
  const loadStr = load['1m'] != null ? `${load['1m']} / ${load['5m']} / ${load['15m']}` : 'N/A';

  // RAM Budget (known static values)
  const RAM_BUDGET = {
    mongoMax: 4096,  // MB
    nodeMax: 2048,   // MB
    osReserved: 1500, // MB
    swapSize: 4096,  // MB
    totalRam: Math.round(ramTotalBytes / (1024 * 1024)), // derive from actual
  };
  // Use 30720 if we can't detect
  if (RAM_BUDGET.totalRam < 1000) RAM_BUDGET.totalRam = 30720;

  const statusColor = (pct: number) => {
    if (pct < 60) return '#22c55e';
    if (pct < 80) return '#f59e0b';
    return '#ef4444';
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <Container>
      <HeaderRow>
        <div>
          <Title>System Maintenance</Title>
          <Subtitle>
            {data
              ? `${sys.hostname || 'Server'} -- ${sys.uptime || 'N/A'} -- Load: ${loadStr}`
              : 'Loading system information...'}
          </Subtitle>
        </div>
        <RefreshButton $spinning={refreshing} onClick={() => fetchData(true)} disabled={refreshing}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M23 4v6h-6" />
            <path d="M1 20v-6h6" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
            <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" />
          </svg>
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </RefreshButton>
      </HeaderRow>

      {error && (
        <ErrorBanner>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          {error}
        </ErrorBanner>
      )}

      {loading ? (
        <LoadingSkeleton />
      ) : data ? (
        <motion.div initial="initial" animate="animate" variants={stagger}>
          {/* ── Live System Overview ─────────────────────────────────── */}
          <SectionTitle>Live System Overview</SectionTitle>
          <Grid $cols={3} variants={stagger}>
            {/* RAM Usage */}
            <Card as={motion.div} variants={fadeUp}>
              <CardTitle>RAM Usage</CardTitle>
              <ProgressRing
                pct={ramPct}
                color={statusColor(ramPct)}
                label={formatBytes(ramUsedBytes)}
                sub={`of ${formatBytes(ramTotalBytes)}`}
              />
              <div style={{ marginTop: 14 }}>
                <StatRow>
                  <StatLabel>Free</StatLabel>
                  <StatValue>{formatBytes(ramFreeBytes)}</StatValue>
                </StatRow>
                <StatRow>
                  <StatLabel>Available</StatLabel>
                  <StatValue>{formatBytes(ramAvailBytes)}</StatValue>
                </StatRow>
              </div>
            </Card>

            {/* Disk Usage */}
            <Card as={motion.div} variants={fadeUp}>
              <CardTitle>Disk Usage</CardTitle>
              <ProgressRing
                pct={diskPct}
                color={statusColor(diskPct)}
                label={formatKB(diskUsedKB)}
                sub={`of ${formatKB(diskTotalKB)}`}
              />
              <div style={{ marginTop: 14 }}>
                <StatRow>
                  <StatLabel>Available</StatLabel>
                  <StatValue>{formatKB(diskAvailKB)}</StatValue>
                </StatRow>
                <StatRow>
                  <StatLabel>Usage</StatLabel>
                  <StatValue>{disk.usePercent || 'N/A'}</StatValue>
                </StatRow>
              </div>
            </Card>

            {/* MongoDB */}
            <Card as={motion.div} variants={fadeUp}>
              <CardTitle>MongoDB</CardTitle>
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#64748b', marginBottom: 4 }}>
                  <span>Cache {formatMB(cacheMB)}</span>
                  <span>{cachePct}%</span>
                </div>
                <BarTrack>
                  <BarFill
                    $color={statusColor(cachePct)}
                    initial={{ width: 0 }}
                    animate={{ width: `${cachePct}%` }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                  />
                </BarTrack>
              </div>
              <StatRow>
                <StatLabel>DB Size</StatLabel>
                <StatValue>{formatMB(dbSizeMB)}</StatValue>
              </StatRow>
              <StatRow>
                <StatLabel>Connections</StatLabel>
                <StatValue>{conns.current} / {(conns.available || 0).toLocaleString()}</StatValue>
              </StatRow>
              <StatRow>
                <StatLabel>Sessions</StatLabel>
                <StatValue>{totalRecordings} total ({sessionRecs.recording || 0} live)</StatValue>
              </StatRow>
              <StatRow>
                <StatLabel>Chunks</StatLabel>
                <StatValue>{(sessionChunks.count || 0).toLocaleString()} ({formatBytes(sessionChunks.size || 0)})</StatValue>
              </StatRow>
            </Card>

            {/* Backend Process */}
            <Card as={motion.div} variants={fadeUp}>
              <CardTitle>Backend Process{backend ? ` (${backend.name})` : ''}</CardTitle>
              {backend ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                    <StatusDot $color={backend.status === 'online' ? '#22c55e' : '#ef4444'} />
                    <span style={{ fontWeight: 600, color: '#1e293b', textTransform: 'capitalize' }}>{backend.status}</span>
                  </div>
                  <StatRow>
                    <StatLabel>Memory</StatLabel>
                    <StatValue>{backendMemMB.toFixed(0)} MB / 2048 MB</StatValue>
                  </StatRow>
                  <StatRow>
                    <StatLabel>CPU</StatLabel>
                    <StatValue>{backend.cpu || 0}%</StatValue>
                  </StatRow>
                  <StatRow>
                    <StatLabel>Uptime</StatLabel>
                    <StatValue>{backendUptimeHrs.toFixed(1)} hrs</StatValue>
                  </StatRow>
                  <StatRow>
                    <StatLabel>Restarts</StatLabel>
                    <StatValue style={{ color: (backend.restarts || 0) > 0 ? '#f59e0b' : '#22c55e' }}>
                      {backend.restarts || 0}
                    </StatValue>
                  </StatRow>
                </>
              ) : (
                <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No PM2 process data available</div>
              )}
            </Card>

            {/* Services */}
            <Card as={motion.div} variants={fadeUp}>
              <CardTitle>Services</CardTitle>
              {serviceList.length > 0 ? serviceList.map((svc) => {
                const active = svc.status === 'active';
                return (
                  <ServiceRow key={svc.name} title={svc.note || ''}>
                    <ServiceName>
                      <StatusDot $color={active ? '#22c55e' : '#ef4444'} />
                      {svc.name}
                    </ServiceName>
                    <ServiceStatus $active={active}>{active ? 'ACTIVE' : svc.status.toUpperCase()}</ServiceStatus>
                  </ServiceRow>
                );
              }) : (
                <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No service data available</div>
              )}
            </Card>
          </Grid>

          {/* ── RAM Budget ──────────────────────────────────────────── */}
          <SectionTitle>RAM Budget Allocation</SectionTitle>
          <Card as={motion.div} variants={fadeUp}>
            <BudgetBarWrapper>
              {(() => {
                const total = RAM_BUDGET.totalRam;
                const allocated = RAM_BUDGET.mongoMax + RAM_BUDGET.nodeMax + RAM_BUDGET.osReserved;
                const available = total - allocated;
                const segments = [
                  { label: `MongoDB (${formatMB(RAM_BUDGET.mongoMax)})`, pct: (RAM_BUDGET.mongoMax / total) * 100, color: '#6366f1' },
                  { label: `Node.js (${formatMB(RAM_BUDGET.nodeMax)})`, pct: (RAM_BUDGET.nodeMax / total) * 100, color: '#3b82f6' },
                  { label: `OS (${formatMB(RAM_BUDGET.osReserved)})`, pct: (RAM_BUDGET.osReserved / total) * 100, color: '#f59e0b' },
                  { label: `Available (${formatMB(available)})`, pct: (available / total) * 100, color: '#22c55e' },
                ];
                return (
                  <>
                    <BarOuter>
                      {segments.map((seg, i) => (
                        <BarSegment
                          key={i}
                          $color={seg.color}
                          $pct={seg.pct}
                          initial={{ width: 0 }}
                          animate={{ width: `${seg.pct}%` }}
                          transition={{ duration: 0.8, delay: i * 0.1, ease: 'easeOut' }}
                        >
                          {seg.pct > 8 ? `${Math.round(seg.pct)}%` : ''}
                        </BarSegment>
                      ))}
                    </BarOuter>
                    <Legend>
                      {segments.map((seg, i) => (
                        <LegendItem key={i}>
                          <LegendSwatch $color={seg.color} />
                          {seg.label}
                        </LegendItem>
                      ))}
                    </Legend>
                    <div style={{ marginTop: 10, fontSize: '0.8rem', color: '#94a3b8' }}>
                      Total RAM: {formatMB(total)} | Swap: {formatMB(RAM_BUDGET.swapSize)}
                    </div>
                  </>
                );
              })()}
            </BudgetBarWrapper>
          </Card>

          {/* ── Maintenance Logs ────────────────────────────────────── */}
          <SectionTitle>Maintenance Logs</SectionTitle>
          <TabBar>
            <Tab $active={logTab === 'cleanup'} onClick={() => setLogTab('cleanup')}>
              Daily Cleanup ({cleanupLogs.length})
            </Tab>
            <Tab $active={logTab === 'health'} onClick={() => setLogTab('health')}>
              Weekly Health Report ({healthLogs.length})
            </Tab>
          </TabBar>

          <AnimatePresence mode="wait">
            <motion.div
              key={logTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {(logTab === 'cleanup' ? cleanupLogs : healthLogs).map((log) => {
                const key = `${logTab}-${log.date}`;
                const isOpen = expandedLogs.has(key);
                return (
                  <LogCard key={key}>
                    <LogHeader onClick={() => toggleLog(key)}>
                      <span>{new Date(log.date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}</span>
                      <LogChevron $open={isOpen}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </LogChevron>
                    </LogHeader>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          style={{ overflow: 'hidden' }}
                        >
                          <LogContent>{log.content}</LogContent>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </LogCard>
                );
              })}
              {(logTab === 'cleanup' ? cleanupLogs : healthLogs).length === 0 && (
                <div style={{ textAlign: 'center', color: '#94a3b8', padding: '32px 0', fontSize: '0.9rem' }}>
                  No logs available yet.
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* ── Scheduled Jobs ──────────────────────────────────────── */}
          {cronJobs.length > 0 && (
            <>
              <SectionTitle>Scheduled Jobs</SectionTitle>
              <CronTable>
                {cronJobs.map((job, i) => (
                  <CronRow key={i}>
                    <CronSchedule>{job.schedule}</CronSchedule>
                    <CronDesc>{job.description}</CronDesc>
                    <CronNext>Next: {nextCronRun(job.schedule)}</CronNext>
                  </CronRow>
                ))}
              </CronTable>
            </>
          )}
        </motion.div>
      ) : null}
    </Container>
  );
};

export default AppMaintenance;
